import { Autocomplete, Box, Button, Grid, TextField } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { IState, ISubject, ITemplate } from "store/types";
import { useDispatch, useSelector } from "react-redux";
import { updateConfig } from "actions/config";
import { AdminActionButtons } from "./AdminActionButtons";
import styled from "styled-components";
import Spinner from "components/spinner";
import { DataGrid, GridColDef, GridColumnHeaderParams, GridRenderCellParams } from "@mui/x-data-grid";
import deleteIcon from "components/content/icons/delete-black.svg";
import { AdminSectionDescription, AdminSectionTitle } from "./ApplicationSettings";
import CloseIcon from "@mui/icons-material/Close";
import { isEmpty, uniq } from "lodash";
import { getTemplates } from "actions/templates";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const ActionButton = styled(Button)`
  font-family: "Roboto";
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 16px;
  padding: 12px 16px;
  margin-top: 64px;
`;

const DeletedTemplate = styled.p`
  font-weight: 200;
`;

export const TemplateSiteList = () => {
  const dispatch = useDispatch();

  // What we need from Redux
  const config = useSelector((state: IState) => state.config);
  const subjects = useSelector((state: IState) => state.subjects);
  const templates = useSelector((state: IState) => state.templates);
  const submittingChanges = useSelector((state: IState) => state.submittingChanges);
  const loading = useSelector(
    (state: IState) => state.loadingSubjects || state.loadingTemplates || state.loadingConfig,
  );

  const mapInitialData = useCallback(() => {
    const rows: any = [];
    config.templateIds.forEach((templateId: string) => {
      const template = templates.find((template: ITemplate) => template.id.toString() === templateId);
      rows.push({ id: Number(templateId), template: template?.name, status: "active" });
    });
    return rows;
  }, [config.templateIds, templates]);

  useEffect(() => {
    if (templates && config && config.templateIds) {
      setRows(mapInitialData());
    }
  }, [templates, config, mapInitialData]);

  const [rows, setRows] = useState([] as any[]);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const addRow = useCallback(() => {
    setRows((oldRows: any) => [...oldRows, { id: -1, template: "", status: "active" }]);
    setIsFormDirty(true);
  }, []);

  const deleteRow = useCallback(
    (templateId: number) => {
      const rowBeingEdited = rows.find((row: any) => row.id === templateId);
      if (rowBeingEdited) {
        rowBeingEdited.status = "deleted";
      }
      const otherRows = rows.filter((row: any) => row.id !== templateId);
      if (rowBeingEdited && otherRows) {
        setRows([...otherRows, rowBeingEdited]);
        setIsFormDirty(true);
      }
      setIsFormDirty(true);
    },
    [rows],
  );

  const undoDeleteRow = useCallback(
    (templateId: number) => {
      const rowBeingEdited = rows.find((row: any) => row.id === templateId);
      if (rowBeingEdited) {
        rowBeingEdited.status = "active";
      }
      const otherRows = rows.filter((row: any) => row.id !== templateId);
      if (rowBeingEdited && otherRows) {
        setRows([...otherRows, rowBeingEdited]);
        setIsFormDirty(true);
      }
      setIsFormDirty(true);
    },
    [rows],
  );

  const handleCancel = useCallback(() => {
    setRows(mapInitialData());
    setIsFormDirty(false);
  }, [mapInitialData]);

  const handleSubmit = useCallback(async () => {
    const templateIds = rows.filter((row: any) => row.status === "active").map((row: any) => row.id.toString());
    await dispatch(updateConfig({ ...config, templateIds: uniq(templateIds) }));
    await dispatch(getTemplates());
    setIsFormDirty(false);
  }, [config, dispatch, rows]);

  const handleSelectTemplate = useCallback(
    (rowData: any) => {
      const rowBeingEdited = rows.find((row: any) => row.id === -1);
      if (rowBeingEdited) {
        rowBeingEdited.id = rowData.id;
        rowBeingEdited.template = rowData.label;
      }
      const otherRows = rows.filter((row: any) => row.id !== -1);
      if (otherRows) {
        setRows(otherRows);
        setIsFormDirty(true);
      }
    },
    [rows],
  );

  const disableAddButton = !isEmpty(rows.find((row: any) => row.id === -1)); // only insert one new row at a time

  const renderTemplate = (params: GridRenderCellParams) => {
    return params.row.id === -1 ? (
      <Autocomplete
        id="subject-admin-autocomplete"
        options={subjectOptions}
        getOptionLabel={(option) => option.label}
        style={{ flex: 1 }}
        sx={{
          "& .MuiOutlinedInput-root": {
            border: "none",
          },
        }}
        renderInput={(params) => <TextField {...params} label="" />}
        onChange={(_event, option) => handleSelectTemplate(option)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    ) : params.row.status === "deleted" ? (
      <DeletedTemplate>{params.row.template}</DeletedTemplate>
    ) : (
      <p>{params.row.template}</p>
    );
  };

  const renderDeleteButton = (params: GridRenderCellParams) => {
    return params.row.status === "active" ? (
      <Button onClick={() => deleteRow(params.row.id)}>
        <img src={deleteIcon} alt="delete" height="18px" />
      </Button>
    ) : (
      <Button onClick={() => undoDeleteRow(params.row.id)}>
        <CloseIcon sx={{ height: "18px", color: "black" }} />
      </Button>
    );
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "",
    },
    {
      field: "status",
      headerName: "",
    },
    {
      field: "template",
      headerName: "Template",
      flex: 1,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerClassName: "template-admin--header",
      renderHeader: (_params: GridColumnHeaderParams) => <strong>{"Template"}</strong>,
      renderCell: renderTemplate,
    },
    {
      field: "deleteAction",
      headerName: "",
      flex: 0.5,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "right",
      headerClassName: "template-admin--header",
      renderCell: renderDeleteButton,
    },
  ];

  // TODO remove from options once selected?
  const subjectOptions = subjects
    ? subjects.map((subject: ISubject) => {
        return { id: subject.id, label: subject.name };
      })
    : [];

  if (submittingChanges || loading) {
    return <Spinner loading={true} />;
  } else
    return (
      <>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} style={{ display: "flex", justifyContent: "left" }}>
            <AdminSectionTitle>Template sites</AdminSectionTitle>
          </Grid>
          <Grid item xs={6} style={{ display: "flex", justifyContent: "right" }}>
            <ActionButton variant="outlined" onClick={addRow} disabled={disableAddButton}>
              + Add template
            </ActionButton>
          </Grid>
        </Grid>
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <div style={{ marginRight: "6px", lineHeight: "1" }}>
            <ErrorOutlineIcon style={{ fontSize: "large", marginTop: 11, paddingRight: 0, color: "#252525" }} />
          </div>
          <div>
            <AdminSectionDescription>
              To add a template, click &apos;Add template&apos; and select an option from the dropdown list that appears
              at the bottom of the list. LMS Moodle sites where you have a Manager or Lecturer role will be available
              for selection. Once selected, the site will appear under the &apos;Template Sites&apos; tab. Click on
              &apos;Save changes&apos; to apply.
            </AdminSectionDescription>
          </div>
        </div>
        <Box
          sx={{
            maxWidth: "100%",
            paddingTop: "30px",
            "& .Mui-odd": {
              backgroundColor: "var(--neutral-100, #FAFAFA)",
            },
            "& .template-admin--header": {
              border: "0px 0px 1px 0px",
              gap: "8px",
              backgroundColor: "var(--neutral-200, #eee)",
            },
            "& .MuiDataGrid-cell": {
              cursor: "pointer",
            },
          }}
        >
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            pageSizeOptions={[10, 25, 50]}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd")}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  id: false,
                  status: false,
                },
              },
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
          ></DataGrid>
        </Box>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        <AdminActionButtons
          onCancel={handleCancel}
          onSave={handleSubmit}
          canSave={!submittingChanges && isFormDirty}
          isFormDirty={isFormDirty}
        />
      </>
    );
};
